import React from 'react';
import type { ButtonSize } from '@wix/design-system';
import { Button, Loader, Tooltip } from '@wix/design-system';
import {
  addAppClick,
  appMarketAppInstallationFailedClient,
  appMarketAppInstallationSuccess,
} from '@wix/bi-logger-app-market-data/v2';
import { InstallAppButtonDataHooks } from './data-hooks';
import type { AppBadge } from '@wix/app-market-services';
import type { SupportedAppStatuses } from '@wix/app-market-services-types';
import { useQueryClient } from '@tanstack/react-query';
import { CustomEvent } from '@wix/app-market-analytics';
import { v4 as generateUuidV4 } from 'uuid';
import {
  useTranslation,
  useEssentials,
  useInstaller,
  useJunkYard,
  useRouterContext,
  useServices,
  useUserContext,
  getAddAppClickBiData,
  getAppMarketAppInstallationFailedClientBiData,
  getAppMarketAppInstallationSuccessBiData,
  QueryDecorator,
  initGoogleAnalytics,
  getAnalyticsParams,
} from '@wix/app-market-core';
import type { RoutePath, RoutePayloadMap } from '@wix/app-market-core-types';
import type { AppPageType } from '@wix/app-market-core-components-types';

export interface InstallAppButtonProps {
  origin: string;
  isDisabled: boolean;
  tooltipContent: string;
  app: {
    id: string;
    isAppWithBenefits: boolean;
    appBadges?: AppBadge[];
    gTag?: string;
  };
  size: ButtonSize;
  status: SupportedAppStatuses;
  isTestMode?: boolean;
  isPermissionsRequired?: boolean;
  openMobileInstallationBlockedModal: () => void;
  appPageType: AppPageType;
}
export function InstallAppButton({
  origin,
  isDisabled,
  tooltipContent,
  app,
  status,
  isTestMode,
  size,
  isPermissionsRequired,
  openMobileInstallationBlockedModal,
  appPageType,
}: InstallAppButtonProps) {
  const { t } = useTranslation();
  const { isMobile } = useUserContext();
  const { route } = useRouterContext();
  const installer = useInstaller();
  const queryClient = useQueryClient();
  const services = useServices();
  const { biLogger, googleAnalytics } = useEssentials();
  const { routerData, goBackToReferrer } = useJunkYard();

  const installAppButtonComponent = ({
    openConsentMethod,
    isLoading,
  }: {
    openConsentMethod?: 'NEW_TAB' | 'NEW_WINDOW' | null;
    isLoading?: boolean;
  }) => {
    const correlationId = generateUuidV4();
    return (
      <Tooltip
        dataHook={InstallAppButtonDataHooks.tooltip}
        inline
        disabled={!tooltipContent}
        content={tooltipContent}
      >
        <Button
          dataHook={InstallAppButtonDataHooks.button}
          disabled={isDisabled || isLoading}
          size={size}
          onClick={() => {
            biLogger.report(
              addAppClick(
                getAddAppClickBiData({
                  appId: app.id,
                  tagType: 'app_page',
                  tagName: app.id,
                  routerData,
                  referrerNameForBI: goBackToReferrer?.referrerNameForBI,
                  appBadges: app.appBadges,
                  origin,
                  correlationId,
                  marketMode:
                    status === 'PUBLISHED'
                      ? 'public'
                      : status === 'APPROVED'
                      ? 'unlisted'
                      : 'test_my_app',
                  appPageType,
                }),
              ),
            );
            const gaInstance = initGoogleAnalytics(googleAnalytics, app.gTag);

            const { referral, referralSectionName, appIndex } =
              (route.payload as RoutePayloadMap[RoutePath.APP_PAGE]) || {};

            gaInstance?.reportCustomEvent({
              name: CustomEvent.INSTALL_CLICK,
              category: 'User Engagement',
              params: getAnalyticsParams({
                referral,
                referralSectionName,
                appIndex,
              }),
            });

            if (isMobile) {
              return openMobileInstallationBlockedModal();
            }
            installer.installApp({
              appId: app.id,
              consentOpeningMethod: openConsentMethod ?? 'NEW_TAB',
              onSuccess: (instanceId) => {
                queryClient.invalidateQueries();
                biLogger.report(
                  appMarketAppInstallationSuccess(
                    getAppMarketAppInstallationSuccessBiData({
                      appId: app.id,
                      tagType: 'app_page',
                      tagName: app.id,
                      instanceId,
                      routerData,
                      referrerNameForBI: goBackToReferrer?.referrerNameForBI,
                      appBadges: app.appBadges,
                      origin,
                      correlationId,
                      searchLocation: (
                        route.payload as RoutePayloadMap[RoutePath.APP_PAGE]
                      )?.searchLocation,
                      appPageType,
                    }),
                  ),
                );
              },
              onFailure: () => {
                biLogger.report(
                  appMarketAppInstallationFailedClient(
                    getAppMarketAppInstallationFailedClientBiData({
                      appId: app.id,
                      tagType: 'app_page',
                      tagName: app.id,
                      appPageType,
                    }),
                  ),
                );
              },
              correlationId,
            });
          }}
        >
          {isLoading ? (
            <Loader size="tiny" />
          ) : isTestMode ? (
            t('solution.page.addToSiteDevVersion')
          ) : isPermissionsRequired ? (
            t('app-page.cta.agree-and-add')
          ) : (
            t('solution.page.addToSite')
          )}
        </Button>
      </Tooltip>
    );
  };

  // Skips unnecessary API calls for the old installer in one-step installations.
  // Remove this condition once all installations are moved to the new installer.
  if (installer.isOneStepInstallation) {
    return installAppButtonComponent({});
  }

  return (
    <QueryDecorator
      queryFn={async () => {
        const webSolutionsBase = await services.getWebSolutionsBase({
          appIds: [app.id],
        });
        const webSolutionBase = webSolutionsBase.findByAppId(app.id);
        return webSolutionBase?.openConsentMethod;
      }}
      queryKey={`install-app-button-${app.id}`}
      csrOnly
    >
      {({ data: openConsentMethod, isLoading }) =>
        installAppButtonComponent({ openConsentMethod, isLoading })
      }
    </QueryDecorator>
  );
}
